import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import Button from '@components/custom/Button';
import Icon from '@components/custom/Icon';
import ModuleHeader from '@components/ModuleHeader';
import ScrollList from '@components/custom/ScrollList';
import Select from '@components/custom/Select';
import Spinner from '@components/custom/Spinner';

import '@assets/css/finances.css';

import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';

import { formatBalance } from '@sm/utils/utils';

const TopUpPayoutBalance = () => {
  const dispatch = useDispatch();

  const inputRef = useRef(null);
  
  const { topUpFeeEnabled } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [merchantId, setMerchantId] = useState(null);
  const [balances, setBalances] = useState({
    availableBalance: 0,
    availableBalanceByPayoutMethod: [],
    payoutBalance: 0
  });
  const [selectedMethod, setSelectedMetod] = useState('');
  const [payoutBalanceMethods, setPayoutBalanceMethods] = useState([]);
  const [payoutFee, setPayoutFee] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    crudActions.get('v1/clients/lookups/balances')
      .then(response => {
        if (response) {
          setCurrencies(response?.localCurrencies);
          setSelectedCurrency(response?.localCurrencies[0]?.value);
          setMerchantId(response?.merchantId);
        }
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  useEffect(() => {
    if (!selectedCurrency) return;

    setIsLoading(true);
    crudActions.get(`v1/merchant-balance/${selectedCurrency}?isGlobalCurrency=${ false }`)
      .then(response => {
        const payoutMethods = response?.availableBalanceByPayoutMethod || [];
        setBalances({
          availableBalance: parseFloat(response?.availableBalance),
          availableBalanceByPayoutMethod: payoutMethods,
          payoutBalance: parseFloat(response?.payoutBalance)
        });
        setPayoutBalanceMethods(
          payoutMethods.map(elem => {
            return {
              label: elem.label,
              value: elem.method
            };
          })
        );
        if (payoutMethods.length === 1) {
          setSelectedMetod(payoutMethods[0].method);
          setPayoutFee(payoutMethods[0].payoutFee || 0);
        }
        setTopUpAmount('');
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedCurrency, dispatch]);

  const formatNumber = (val) => {
    if (!val) return '';
    const [intPart, decimalPart] = val.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart !== undefined
      ? `${formattedInt}.${decimalPart.slice(0, 2)}`
      : formattedInt;
  };

  const stripFormatting = (val) => val.replace(/,/g, '');

  const handleChange = (e, fieldName) => {
    if (fieldName === 'method') {
      setSelectedMetod(e?.value || '');
      const foundMethod = balances.availableBalanceByPayoutMethod.find(elem => elem.method === e?.value);
      setPayoutFee(foundMethod?.payoutFee || 0);
      return;
    }
    const input = e.target;
    let raw = stripFormatting(input.value);

    // Only allow digits and at most one decimal point
    if (!/^\d*\.?\d*$/.test(raw)) return;

    // Restrict to max 2 decimals
    if (raw.includes('.')) {
      const parts = raw.split('.');
      if (parts[1].length > 2) {
        raw = parts[0] + '.' + parts[1].slice(0, 2);
      }
    }

    if (parseFloat(raw) > balances.availableBalance) return;
    setTopUpAmount(raw);

    const formatted = formatNumber(raw);

    const prevPos = input.selectionStart;
    const prevLength = input.value.length;

    requestAnimationFrame(() => {
      input.value = formatted;
      const newLength = formatted.length;
      const posDiff = newLength - prevLength;
      input.setSelectionRange(prevPos + posDiff, prevPos + posDiff);
    });
  };

  const handleKeyDown = (e) => {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End',
      '.', // allow decimal point
    ];

    // Allow Ctrl/Command + keys (copy/paste/select all)
    if (e.ctrlKey || e.metaKey) return;

    if (
      !allowedKeys.includes(e.key) &&
      !/^\d$/.test(e.key) // digit keys only
    ) {
      e.preventDefault();
    }
  };

  const submitTopUpRequest = () => {
    setIsLoading(true);
    const data = {
      availableBalanceAmount: balances.availableBalance,
      currency: selectedCurrency,
      merchantId,
      method: selectedMethod,
      payoutMethod: selectedMethod,
      date: moment().format('DD.MM.YYYY HH:mm:ss'),
      payoutFee,
      topUpAmount
    };
    crudActions.post(`v1/data/input/payout/top-up/request`, data)
      .then(() => {
        dispatch(showAlert('success', 'Top-up is waiting for approval'));
        setTopUpAmount('');
        setSelectedMetod('');
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isTopUpEntered = topUpAmount > 0;
  const calculatedPayoutFee = topUpAmount * (payoutFee / 100);
  let topUpAmountPlusCalculatedFee = topUpAmount;
  if (topUpFeeEnabled) {
    topUpAmountPlusCalculatedFee = topUpAmount + calculatedPayoutFee;
  }

  return (
    <div className='col-12 module finances topUp'>
      <div className='component shadow mb-4 position-relative'>
        <ModuleHeader
          id='send_money'
          title='Top-Up Payout Balance'
          actions={
            <ScrollList
              items={ currencies }
              selectedItem={ selectedCurrency }
              onClick={ (value) => setSelectedCurrency(value) }
            />
          }
        />

        {isLoading && <Spinner /> }

        <div className='row d-flex align-items-stretch info'>
          <div className='col-sm-12 col-md-4 item-wrap'>
            <div className='item item1'>
              <div className='header'>
                <Icon name='finance' />
                Available Balance
              </div>
              <div className='body'>
                <div>
                  <span className='currency'>{ selectedCurrency }</span>
                  <span className={ `number ${isTopUpEntered ? 'gold' : ''}` }>
                    { formatBalance(balances.availableBalance - Number(topUpAmount)) }
                  </span>
                </div>
                {isTopUpEntered && (
                  <div>
                    <span className='chevron gold'>Future Balance</span>
                  </div>
                )}
              </div>
              <div className='arrow'>
                <Icon name='keyboard_double_arrow_right' style={ { marginRight: 0 } } color='#8a8a8a' />
              </div>
            </div>
          </div>
          <div className='col-sm-12 col-md-4 item-wrap'>
            <div className='item item2'>
              <div className='header d-flex justify-content-between'>
                <div className='d-flex align-items-end'>
                  <Icon name='payment_arrow_down' style={ { rotate: '180deg', margin: '0 5px 0 0' } } />
                  Top-up Amount
                </div>
                <div className='fx-pairs'>
                  <Select
                    id="method"
                    value={ selectedMethod }
                    disabled={ isLoading }
                    placeholder='Select Method'
                    onChange={ (value) => handleChange(value, 'method') }
                    options={ payoutBalanceMethods }
                  />
                </div>
              </div>
              <div className='body d-flex justify-content-between align-items-end'>
                <div className='amount-wrap'>
                  <div>Enter amount</div>
                  <div className='d-flex align-items-center'>
                    <span className='currency'>{ selectedCurrency }</span>
                    <input
                      id='top-up'
                      ref={ inputRef }
                      className="amount-input"
                      onKeyDown={ handleKeyDown }
                      onChange={ handleChange }
                      value={ formatNumber(topUpAmount) }
                      disabled={ !selectedMethod }
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <Button
                  label='Request'
                  disabled={ !balances.availableBalance || !topUpAmount || !selectedMethod }
                  onClick={ () => submitTopUpRequest() }
                />
              </div>
              {topUpFeeEnabled && (
                <div className='payout-fee mt-3'>
                  {selectedCurrency} {formatBalance(calculatedPayoutFee)} Payout Fee
                </div>
              )}
              <div className='arrow'>
                <Icon name='keyboard_double_arrow_right' style={ { marginRight: 0 } } color='#8a8a8a' />
              </div>
            </div>
          </div>
          <div className='col-sm-12 col-md-4 item-wrap'>
            <div className='item item3'>
              <div className='header d-flex justify-content-between'>
                <div className='d-flex align-items-end'>
                  <Icon name='equalizer' style={ { margin: '0 5px 0 0' } } />
                  Payout Balance
                </div>
              </div>
              <div className='body'>
                <div>
                  <span className='currency'>{ selectedCurrency }</span>
                  <span className={ `number ${isTopUpEntered ? 'blue' : ''}` }>
                    { formatBalance(balances.payoutBalance + Number(topUpAmountPlusCalculatedFee)) }
                  </span>
                </div>
                {isTopUpEntered && (
                  <div>
                    <span className='chevron blue'>Future Balance</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopUpPayoutBalance;